/**
 * Médico Remitente Form Dialog - Create/Edit Referring Doctor
 *
 * Formulario para crear o editar médicos remitentes.
 * Usa TanStack Query para las mutaciones.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LocationSelector } from "@/components/ui/location-selector";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreateMedicoRemitente,
  useUpdateMedicoRemitente,
  type MedicoRemitente,
} from "@/hooks/use-medicos-remitentes";
import { ESPECIALIDADES_MEDICAS } from "@/hooks/use-medicos-remitentes";

interface MedicoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  medico: MedicoRemitente | null;
  onSuccess: (isNew: boolean) => void;
}

// Schema de validación
const medicoSchema = z.object({
  primer_nombre: z.string().min(2, "medicos.validation.firstNameRequired"),
  segundo_nombre: z.string().optional(),
  primer_apellido: z.string().optional(),
  segundo_apellido: z.string().optional(),
  especialidad: z.string().optional(),
  telefono: z.string().optional(),
  celular: z.string().optional(),
  email: z.string().email("medicos.validation.emailInvalid").optional().or(z.literal("")),
  departamento: z.string().optional(),
  ciudad: z.string().optional(),
  clinica_hospital: z.string().optional(),
});

type FormData = z.infer<typeof medicoSchema>;

export function MedicoFormDialog({ open, onOpenChange, medico, onSuccess }: MedicoFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!medico;

  // TanStack Query mutations
  const createMutation = useCreateMedicoRemitente({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdateMedicoRemitente({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(medicoSchema),
    defaultValues: {
      primer_nombre: "",
      segundo_nombre: "",
      primer_apellido: "",
      segundo_apellido: "",
      especialidad: "",
      telefono: "",
      celular: "",
      email: "",
      departamento: "",
      ciudad: "",
      clinica_hospital: "",
    },
  });

  const departamento = watch("departamento");
  const ciudad = watch("ciudad");

  // Reset form when dialog opens/closes or when medico data loads
  useEffect(() => {
    if (open && medico) {
      // Only reset when we have actual medico data
      reset({
        primer_nombre: medico.primer_nombre,
        segundo_nombre: medico.segundo_nombre || "",
        primer_apellido: medico.primer_apellido,
        segundo_apellido: medico.segundo_apellido || "",
        especialidad: medico.especialidad || "",
        telefono: medico.telefono || "",
        celular: medico.celular || "",
        email: medico.email || "",
        departamento: medico.departamento || "",
        ciudad: medico.ciudad || "",
        clinica_hospital: medico.clinica_hospital || "",
      });
      setError(null);
    } else if (open && !medico) {
      // New medico - reset to empty values
      // But only if we're not waiting for data (selectedMedicoId would be set if editing)
      reset({
        primer_nombre: "",
        segundo_nombre: "",
        primer_apellido: "",
        segundo_apellido: "",
        especialidad: "",
        telefono: "",
        celular: "",
        email: "",
        departamento: "",
        ciudad: "",
        clinica_hospital: "",
      });
      setError(null);
    }
  }, [open, medico, reset]);

  const onSubmit = (data: FormData) => {
    setError(null);

    const payload = {
      primer_nombre: data.primer_nombre,
      segundo_nombre: data.segundo_nombre || undefined,
      primer_apellido: data.primer_apellido ?? "",
      segundo_apellido: data.segundo_apellido || undefined,
      especialidad: data.especialidad || undefined,
      telefono: data.telefono || undefined,
      celular: data.celular || undefined,
      email: data.email || undefined,
      departamento: data.departamento || undefined,
      ciudad: data.ciudad || undefined,
      clinica_hospital: data.clinica_hospital || undefined,
    };

    if (isEditing && medico) {
      updateMutation.mutate({ id: medico.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t("medicos.editDoctor") : t("medicos.newDoctor")}</DialogTitle>
          <DialogDescription>
            {isEditing ? t("medicos.editDoctorDesc") : t("medicos.newDoctorDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Nombres */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primer_nombre">{t("medicos.fields.firstName")}</Label>
                <Input
                  id="primer_nombre"
                  placeholder={isEditing ? undefined : "Carlos"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("primer_nombre")}
                />
                {errors.primer_nombre && (
                  <p className="text-sm text-destructive">
                    {t(errors.primer_nombre.message as string)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="segundo_nombre">{t("medicos.fields.secondName")}</Label>
                <Input
                  id="segundo_nombre"
                  placeholder={isEditing ? undefined : "Alberto"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("segundo_nombre")}
                />
              </div>
            </div>

            {/* Apellidos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primer_apellido">{t("medicos.fields.lastName")}</Label>
                <Input
                  id="primer_apellido"
                  placeholder={isEditing ? undefined : "Martínez"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("primer_apellido")}
                />
                {errors.primer_apellido && (
                  <p className="text-sm text-destructive">
                    {t(errors.primer_apellido.message as string)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="segundo_apellido">{t("medicos.fields.secondLastName")}</Label>
                <Input
                  id="segundo_apellido"
                  placeholder={isEditing ? undefined : "López"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("segundo_apellido")}
                />
              </div>
            </div>

            {/* Especialidad */}
            <div className="space-y-2">
              <Label htmlFor="especialidad">{t("medicos.fields.specialty")}</Label>
              <Select
                value={watch("especialidad") || ""}
                onValueChange={(value) => setValue("especialidad", value)}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("medicos.selectSpecialty", "Seleccione especialidad")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ESPECIALIDADES_MEDICAS.map((esp) => (
                    <SelectItem key={esp.value} value={esp.value}>
                      {esp.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contacto */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefono">{t("medicos.fields.phone")}</Label>
                <Input
                  id="telefono"
                  placeholder={isEditing ? undefined : "+504 2222-3333"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("telefono")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="celular">{t("medicos.fields.cellphone")}</Label>
                <Input
                  id="celular"
                  placeholder={isEditing ? undefined : "+504 9999-8888"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("celular")}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{t("medicos.fields.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={isEditing ? undefined : "doctor@email.com"}
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
              )}
            </div>

            {/* Ubicación */}
            <LocationSelector
              departamento={departamento || ""}
              ciudad={ciudad || ""}
              onDepartamentoChange={(value) => setValue("departamento", value)}
              onCiudadChange={(value) => setValue("ciudad", value)}
            />

            {/* Clínica/Hospital */}
            <div className="space-y-2">
              <Label htmlFor="clinica_hospital">{t("medicos.fields.clinic")}</Label>
              <Input
                id="clinica_hospital"
                placeholder={isEditing ? undefined : "Hospital General, Clínica XYZ, etc."}
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                {...register("clinica_hospital")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
